import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const TeilnehmerLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [systemOnline, setSystemOnline] = useState(true);
  const [fixedTexts, setFixedTexts] = useState({});
  
  // Two-step login state
  const [step, setStep] = useState(1); // 1 = Name, 2 = Birthdate
  const [vorname, setVorname] = useState('');
  const [nachname, setNachname] = useState('');
  const [teilnehmerId, setTeilnehmerId] = useState(null);
  const [hasExamResults, setHasExamResults] = useState(false);
  const [currentModus, setCurrentModus] = useState('Anmeldung');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Geburtsdatum separate fields
  const [geburtTag, setGeburtTag] = useState('');
  const [geburtMonat, setGeburtMonat] = useState('');
  const [geburtJahr, setGeburtJahr] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('teilnehmer_token');
    if (token) {
      navigate('/teilnehmer/dashboard');
      return;
    }
    checkSystemStatus();
    fetchFixedTexts();
  }, [navigate]);

  const checkSystemStatus = async () => {
    try {
      const response = await axios.get(`${API}/public/system-status`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
      setSystemOnline(response.data.online);
    } catch (error) {
      console.error('Error checking system status:', error);
      setSystemOnline(true);
    }
  };

  const fetchFixedTexts = async () => {
    try {
      const response = await axios.get(`${API}/public/fixed-texts`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
      setFixedTexts(response.data);
    } catch (error) {
      console.error('Error fetching fixed texts:', error);
    }
  };

  // Step 1: Check name
  const handleCheckName = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      const response = await axios.post(`${API}/auth/teilnehmer/check`, {
        vorname: vorname.trim(),
        nachname: nachname.trim()
      }, {
        headers: { 'Cache-Control': 'no-cache' }
      });

      const { found, needs_birthdate, teilnehmer_id, has_exam_results, modus } = response.data;

      if (!found) {
        // Scenario 1: Name not found
        setErrorMessage('Wir konnten dich leider nicht finden. Bitte überprüfe deinen Vor- und Nachnamen.');
        return;
      }

      if (!needs_birthdate) {
        // No birthdate needed -> Direct login
        const loginResponse = await axios.post(`${API}/auth/teilnehmer/login-direct`, {
          vorname: vorname.trim(),
          nachname: nachname.trim()
        }, {
          headers: { 'Cache-Control': 'no-cache' }
        });

        localStorage.setItem('teilnehmer_token', loginResponse.data.access_token);
        toast.success('Willkommen!');
        navigate('/teilnehmer/dashboard');
        return;
      }

      // Birthdate needed -> Go to step 2
      setTeilnehmerId(teilnehmer_id);
      setHasExamResults(has_exam_results);
      setCurrentModus(modus || 'Anmeldung');
      setStep(2);

    } catch (error) {
      if (error.response?.status === 503) {
        toast.error('System derzeit nicht verfügbar');
        setSystemOnline(false);
      } else {
        setErrorMessage(error.response?.data?.detail || 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify birthdate
  const handleVerifyBirthdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    if (!geburtTag || !geburtMonat || !geburtJahr) {
      setErrorMessage('Bitte gib dein vollständiges Geburtsdatum ein.');
      setLoading(false);
      return;
    }

    const geburtsdatum = `${geburtJahr}-${geburtMonat.padStart(2, '0')}-${geburtTag.padStart(2, '0')}`;

    try {
      const response = await axios.post(`${API}/auth/teilnehmer/login-with-birthdate`, {
        teilnehmer_id: teilnehmerId,
        geburtsdatum: geburtsdatum
      }, {
        headers: { 'Cache-Control': 'no-cache' }
      });

      localStorage.setItem('teilnehmer_token', response.data.access_token);
      toast.success('Willkommen!');
      navigate('/teilnehmer/dashboard');

    } catch (error) {
      if (error.response?.status === 503) {
        toast.error('System derzeit nicht verfügbar');
        setSystemOnline(false);
      } else {
        setErrorMessage(error.response?.data?.detail || 'Das Geburtsdatum stimmt nicht. Bitte überprüfe deine Eingabe.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setTeilnehmerId(null);
    setErrorMessage('');
    setGeburtTag('');
    setGeburtMonat('');
    setGeburtJahr('');
  };

  // Generate year options (last 100 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
  
  // Months
  const months = [
    { value: '1', label: 'Jänner' },
    { value: '2', label: 'Februar' },
    { value: '3', label: 'März' },
    { value: '4', label: 'April' },
    { value: '5', label: 'Mai' },
    { value: '6', label: 'Juni' },
    { value: '7', label: 'Juli' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'Oktober' },
    { value: '11', label: 'November' },
    { value: '12', label: 'Dezember' },
  ];

  // Days (1-31)
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  if (!systemOnline) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="w-full max-w-md text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_musictheory-admin/artifacts/fetmifax_logo_standard.svg" 
            alt="Musikschule Ybbsfeld Logo" 
            className="h-32 sm:h-40 lg:h-48 w-auto mx-auto mb-12"
          />
          <div className="mx-auto w-32 h-32 rounded-full bg-red-50 flex items-center justify-center mb-8">
            <svg className="w-16 h-16 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.636 5.636a9 9 0 1012.728 0M12 3v9" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {fixedTexts.offline_text || "Derzeit Offline"}
          </h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-6 sm:mb-12">
          <img 
            src="https://customer-assets.emergentagent.com/job_musictheory-admin/artifacts/fetmifax_logo_standard.svg" 
            alt="Musikschule Ybbsfeld Logo" 
            className="h-24 sm:h-40 lg:h-48 w-auto mx-auto mb-4 sm:mb-8"
          />
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-2">
            Theoriekurse
          </h1>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-start gap-3">
            <AlertCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 font-medium">{errorMessage}</p>
          </div>
        )}

        {/* Step 1: Name Input */}
        {step === 1 && (
          <form onSubmit={handleCheckName} className="space-y-4 sm:space-y-6">
            <div>
              <Input
                id="vorname"
                type="text"
                data-testid="login-vorname-input"
                value={vorname}
                onChange={(e) => setVorname(e.target.value)}
                required
                className="w-full h-12 sm:h-16 text-base sm:text-lg border-2 border-gray-300 rounded-xl focus:border-[#c41e3a] focus:ring-4 focus:ring-red-100 transition-all bg-white shadow-sm"
                placeholder="Vorname"
              />
            </div>
            <div>
              <Input
                id="nachname"
                type="text"
                data-testid="login-nachname-input"
                value={nachname}
                onChange={(e) => setNachname(e.target.value)}
                required
                className="w-full h-12 sm:h-16 text-base sm:text-lg border-2 border-gray-300 rounded-xl focus:border-[#c41e3a] focus:ring-4 focus:ring-red-100 transition-all bg-white shadow-sm"
                placeholder="Nachname"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full h-12 sm:h-16 bg-[#c41e3a] hover:bg-[#a01729] text-white font-bold text-base sm:text-lg rounded-xl shadow-lg hover:shadow-xl transition-all mt-6 sm:mt-8"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 sm:h-6 sm:w-6 animate-spin" />
                  Einen Moment...
                </>
              ) : (
                'Weiter'
              )}
            </Button>
          </form>
        )}

        {/* Step 2: Birthdate Input */}
        {step === 2 && (
          <form onSubmit={handleVerifyBirthdate} className="space-y-4 sm:space-y-6">
            {/* Back Button */}
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Zurück</span>
            </button>

            {/* Info Text - different based on mode */}
            <div className={`p-4 border-2 rounded-xl mb-6 ${
              currentModus === 'Prüfung' && hasExamResults 
                ? 'bg-green-50 border-green-200' 
                : 'bg-blue-50 border-blue-200'
            }`}>
              <p className={`font-medium text-center ${
                currentModus === 'Prüfung' && hasExamResults 
                  ? 'text-green-800' 
                  : 'text-blue-800'
              }`}>
                Hallo {vorname}! {currentModus === 'Prüfung' && hasExamResults ? '🎉' : '👋'}<br />
                {currentModus === 'Prüfung' && hasExamResults 
                  ? 'Dein Prüfungsergebnis ist da! Bitte gib zur Bestätigung dein Geburtsdatum ein.'
                  : 'Bitte gib zur Anmeldung dein Geburtsdatum ein.'
                }
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm sm:text-base font-semibold text-gray-700 pl-1">
                Geburtsdatum
              </Label>
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {/* Tag */}
                <Select value={geburtTag} onValueChange={setGeburtTag}>
                  <SelectTrigger 
                    className="h-12 sm:h-14 text-base border-2 border-gray-300 rounded-xl focus:border-[#c41e3a] bg-white shadow-sm"
                    data-testid="login-geburt-tag"
                  >
                    <SelectValue placeholder="Tag" />
                  </SelectTrigger>
                  <SelectContent>
                    {days.map(day => (
                      <SelectItem key={day} value={String(day)}>
                        {day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Monat */}
                <Select value={geburtMonat} onValueChange={setGeburtMonat}>
                  <SelectTrigger 
                    className="h-12 sm:h-14 text-base border-2 border-gray-300 rounded-xl focus:border-[#c41e3a] bg-white shadow-sm"
                    data-testid="login-geburt-monat"
                  >
                    <SelectValue placeholder="Monat" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map(month => (
                      <SelectItem key={month.value} value={month.value}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Jahr */}
                <Select value={geburtJahr} onValueChange={setGeburtJahr}>
                  <SelectTrigger 
                    className="h-12 sm:h-14 text-base border-2 border-gray-300 rounded-xl focus:border-[#c41e3a] bg-white shadow-sm"
                    data-testid="login-geburt-jahr"
                  >
                    <SelectValue placeholder="Jahr" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    {years.map(year => (
                      <SelectItem key={year} value={String(year)}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full h-12 sm:h-16 bg-[#c41e3a] hover:bg-[#a01729] text-white font-bold text-base sm:text-lg rounded-xl shadow-lg hover:shadow-xl transition-all mt-6 sm:mt-8"
              disabled={loading}
              data-testid="login-birthdate-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 sm:h-6 sm:w-6 animate-spin" />
                  Einen Moment...
                </>
              ) : (
                currentModus === 'Prüfung' && hasExamResults ? 'Ergebnis anzeigen' : 'Anmelden'
              )}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default TeilnehmerLogin;
